import { FormEvent, useId, useState } from 'react';

import { PersonAvatar } from 'components/person-avatar';
import { MINIMO_PARA_BUSCAR, useBuscaAoDigitar } from 'hooks/useBuscaAoDigitar';
import Button from 'components/ui/button';
import { ModalFrame } from 'components/ui/modal-frame';
import { iniciaisDoFunci } from 'pages/Rede/feed-calculos';
import { FOCO_VISIVEL } from 'utils/foco';

import { RostoDoFunci } from '../minha-equipe-calculos';

/** Pessoa encontrada pela busca do modal, já no formato de exibição. */
export interface PessoaEncontrada<T> {
  id: string;
  nome: string;
  chave: string;
  /** "{diretoria} · {funcao}". */
  detalhe: string;
  /** Foto ou avatar; sem eles, as iniciais do nome (MEQ2-08). */
  rosto?: RostoDoFunci;
  /** Resposta original do serviço, devolvida ao confirmar. */
  dados: T;
}

export interface ModalDeBuscaProps<T> {
  isOpen: boolean;
  titulo: string;
  texto: string;
  rotuloDoCampo: string;
  placeholder: string;
  aviso?: string;
  rotuloDoConfirmar: string;
  /** Rejeita em falha da busca. */
  buscar: (termo: string) => Promise<PessoaEncontrada<T>[]>;
  /** Motivo que impede confirmar a pessoa escolhida (regra de `cod_etapa`), ou null. */
  bloqueio?: (pessoa: PessoaEncontrada<T>) => string | null;
  /** Gravação; rejeita em falha e o modal fica aberto. */
  confirmar: (pessoa: PessoaEncontrada<T>) => Promise<void> | void;
  onFechar: () => void;
}

const CHAVE_DIGITADA = /^F\d/i;

/** Máscara da chave dos modais antigos (F9999999): "f123" vira "F123"; nome segue livre. */
export function mascaraDaChave(valor: string): string {
  if (!CHAVE_DIGITADA.test(valor)) return valor;
  return `F${valor.slice(1).replace(/\D/g, '').slice(0, 7)}`;
}

const ICONE_BUSCA = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-5-5" />
  </svg>
);

const ICONE_INFO = (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--brand-blue)"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    className="mt-px shrink-0"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

type EstadoDaBusca = 'inicio' | 'buscando' | 'pronta' | 'erro';

function CorpoDaBusca<T>({
  texto,
  rotuloDoCampo,
  placeholder,
  aviso,
  rotuloDoConfirmar,
  buscar,
  bloqueio,
  confirmar,
  onFechar,
  textoId,
}: Omit<ModalDeBuscaProps<T>, 'isOpen' | 'titulo'> & { textoId: string }) {
  const campoId = useId();
  const [estado, setEstado] = useState<EstadoDaBusca>('inicio');
  const [resultados, setResultados] = useState<PessoaEncontrada<T>[]>([]);
  const [escolhida, setEscolhida] = useState<PessoaEncontrada<T> | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erroAoConfirmar, setErroAoConfirmar] = useState(false);

  const motivoDoBloqueio = escolhida && bloqueio ? bloqueio(escolhida) : null;

  // MEQ2-11: busca enquanto se digita (2 letras, 300 ms), como nas outras buscas de pessoas; Enter e a lupa buscam
  // na hora; esvaziar o campo volta ao início.
  async function pesquisar(termo: string, ehAtual: () => boolean) {
    setEscolhida(null);
    if (!termo) {
      setResultados([]);
      setEstado('inicio');
      return;
    }

    setEstado('buscando');
    try {
      const encontradas = await buscar(termo);
      // Só a busca mais recente vale: a resposta atrasada de outra não troca a lista nem a escolhida.
      if (!ehAtual()) return;
      setResultados(encontradas);
      // Um resultado só já vem escolhido, como nos modais antigos.
      setEscolhida(encontradas.length === 1 ? encontradas[0] : null);
      setEstado('pronta');
    } catch {
      if (!ehAtual()) return;
      setResultados([]);
      setEstado('erro');
    }
  }

  const { texto: termoDigitado, digitar, buscarAgora, limpar } = useBuscaAoDigitar(pesquisar);

  function digitarNoCampo(valor: string) {
    const texto = mascaraDaChave(valor);
    const curtoDemais = texto.trim().length > 0 && texto.trim().length < MINIMO_PARA_BUSCAR;
    // Abaixo de 2 letras não há busca: a lista do termo anterior sai, para não confirmar quem não corresponde ao
    // texto, e o próximo termo é buscado mesmo que repita o anterior.
    if (curtoDemais) {
      limpar();
      setResultados([]);
      setEscolhida(null);
      setEstado('inicio');
    }
    digitar(texto);
  }

  function enviarBusca(evento: FormEvent) {
    evento.preventDefault();
    buscarAgora();
  }

  async function enviar() {
    if (!escolhida) return;

    setEnviando(true);
    setErroAoConfirmar(false);
    try {
      await confirmar(escolhida);
    } catch {
      setErroAoConfirmar(true);
      setEnviando(false);
    }
  }

  return (
    <div className="flex flex-col gap-4 px-5 py-6 lg:px-7">
      <p id={textoId} className="m-0 text-[12.5px] leading-[1.6] text-content-soft [text-wrap:pretty]">
        {texto}
      </p>

      <form role="search" onSubmit={enviarBusca}>
        <label htmlFor={campoId} className="mb-1.5 block text-[11px] font-semibold text-content-soft">
          {rotuloDoCampo}
        </label>
        <div className="relative">
          {/* SPEC_DEVIATION: borda `content-soft` (design #E5E7EB, 1,24:1) para o limite do campo passar 3:1, como no Feed. */}
          <input
            id={campoId}
            type="search"
            value={termoDigitado}
            onChange={evento => digitarNoCampo(evento.target.value)}
            placeholder={placeholder}
            className="box-border w-full rounded-jj-field border border-solid border-content-soft bg-brand-white py-[11px] pl-3.5 pr-11 font-text text-[13px] text-content outline-none focus:border-brand-blue focus:shadow-[0_0_0_3px_rgba(70,94,255,0.14)]"
          />
          <button
            type="submit"
            aria-label="Buscar"
            className={`absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-jj-field border-0 bg-transparent text-brand-blue hover:bg-surface-100 ${FOCO_VISIVEL}`}
          >
            {ICONE_BUSCA}
          </button>
        </div>
      </form>

      <div aria-live="polite" aria-busy={estado === 'buscando'}>
        {estado === 'buscando' && <p className="m-0 text-xs text-content-soft">Buscando…</p>}
        {estado === 'erro' && <p className="m-0 text-xs font-bold text-urgent">Não foi possível buscar agora. Tente de novo.</p>}
        {estado === 'pronta' && !resultados.length && (
          <p className="m-0 text-xs text-content-soft">
            Nenhum resultado. Experimente buscar por nome e sobrenome ou pela chave.
          </p>
        )}
        {estado === 'pronta' && resultados.length > 0 && (
          <fieldset className="m-0 border-0 p-0">
            <legend className="sr-only">Resultados da busca</legend>
            <ul className="m-0 flex max-h-[220px] list-none flex-col gap-2 overflow-y-auto p-0">
              {resultados.map(pessoa => (
                <li key={pessoa.id}>
                  <label
                    className={`flex cursor-pointer items-center gap-3 rounded-[10px] border border-solid px-3.5 py-2.5 ${
                      escolhida?.id === pessoa.id
                        ? 'border-brand-blue bg-surface-100'
                        : 'border-surface-300 bg-brand-white hover:border-brand-blue'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pessoa-encontrada"
                      checked={escolhida?.id === pessoa.id}
                      onChange={() => setEscolhida(pessoa)}
                      className={`h-4 w-4 shrink-0 accent-[var(--brand-blue)] ${FOCO_VISIVEL}`}
                    />
                    <PersonAvatar
                      image={pessoa.rosto?.image}
                      avatar={pessoa.rosto?.avatar}
                      avatarType={pessoa.rosto?.avatarType}
                      initials={iniciaisDoFunci(pessoa.nome)}
                      size={34}
                    />
                    <span className="min-w-0">
                      <span className="block text-[13px] font-bold text-content">
                        {pessoa.nome} <span className="font-normal text-content-soft">({pessoa.chave})</span>
                      </span>
                      {pessoa.detalhe && <span className="block text-[11px] text-content-soft">{pessoa.detalhe}</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}
      </div>

      {motivoDoBloqueio && (
        <p role="alert" className="m-0 text-xs font-bold text-urgent">
          {motivoDoBloqueio}
        </p>
      )}

      {aviso && (
        <div className="flex items-start gap-2.5 rounded-[10px] bg-brand-yellow-light px-[15px] py-[13px]">
          {ICONE_INFO}
          <p className="m-0 text-[11.5px] leading-[1.55] text-content [text-wrap:pretty]">{aviso}</p>
        </div>
      )}

      {erroAoConfirmar && (
        <p role="alert" className="m-0 text-xs font-bold text-urgent">
          Não foi possível registrar a solicitação. Tente de novo.
        </p>
      )}

      <div className="flex items-center justify-end gap-2.5">
        <Button variant="secundario" size="sm" onClick={onFechar}>
          Voltar
        </Button>
        <Button variant="solido-destaque" size="sm" onClick={enviar} disabled={!escolhida || enviando || !!motivoDoBloqueio}>
          {rotuloDoConfirmar}
        </Button>
      </div>
    </div>
  );
}

/**
 * Moldura comum dos modais de adicionar funcionário, alterar gestor e equipe lateral: busca,
 * escolha de um resultado e confirmação. O conteúdo só monta aberto, então cada abertura começa limpa.
 */
export default function ModalDeBusca<T>({ isOpen, titulo, ...corpo }: ModalDeBuscaProps<T>) {
  const textoId = useId();

  return (
    <ModalFrame isOpen={isOpen} size="sm" title={titulo} descriptionId={textoId} onClose={corpo.onFechar}>
      <CorpoDaBusca {...corpo} textoId={textoId} />
    </ModalFrame>
  );
}
